import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { VEHICLE_BRANDS, VEHICLE_MODELS } from "@/lib/vehicles";

export const selectCls = "h-10 w-full rounded-sm border border-input bg-background px-3 text-sm outline-none focus:border-primary";

export function ClientDialog({ open, onOpenChange, initial, onSaved }: { open: boolean; onOpenChange: (v: boolean) => void; initial?: any; onSaved: (c: any) => void }) {
  const empty = { kind: "particulier", name: "", phone: "", email: "", address: "", nif: "", rc: "", notes: "" };
  const [f, setF] = useState<any>(empty);
  const [dup, setDup] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setF(initial ? { ...empty, ...initial } : empty);
      setDup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const r = initial?.id ? await api.put(`/clients/${initial.id}`, f) : await api.post("/clients", f);
      toast.success("Client enregistré");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(errMsg(e));
      setDup(e?.response?.data?.detail?.existing_id || null);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{initial?.id ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type">
            <select className={selectCls} value={f.kind} onChange={set("kind")} data-testid="client-kind-select">
              <option value="particulier">Particulier</option>
              <option value="entreprise">Entreprise</option>
            </select>
          </Field>
          <Field label="Nom / raison sociale"><Input value={f.name} onChange={set("name")} data-testid="client-name-input" /></Field>
          <Field label="Téléphone"><Input value={f.phone} onChange={set("phone")} placeholder="0550 00 00 00" data-testid="client-phone-input" /></Field>
          <Field label="Email"><Input value={f.email} onChange={set("email")} data-testid="client-email-input" /></Field>
          <Field label="Adresse" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="client-address-input" /></Field>
          {f.kind === "entreprise" && (
            <>
              <Field label="NIF"><Input value={f.nif} onChange={set("nif")} data-testid="client-nif-input" /></Field>
              <Field label="RC"><Input value={f.rc} onChange={set("rc")} data-testid="client-rc-input" /></Field>
            </>
          )}
        </div>
        {dup && <Link to={`/clients/${dup}`} onClick={() => onOpenChange(false)} className="text-sm text-primary underline" data-testid="client-duplicate-link">Ouvrir la fiche existante</Link>}
        <Button onClick={save} className="h-11" data-testid="client-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

export function VehicleDialog({ open, onOpenChange, clientId, initial, onSaved }: { open: boolean; onOpenChange: (v: boolean) => void; clientId: string; initial?: any; onSaved: (v: any) => void }) {
  const empty = { plate: "", vin: "", brand: "", model: "", year: "", fuel: "Essence", color: "", mileage: "" };
  const [f, setF] = useState<any>(empty);
  const [dup, setDup] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setF(initial ? { ...empty, ...initial, vin: initial.vin || "" } : empty);
      setDup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, client_id: clientId, year: f.year ? Number(f.year) : null, mileage: f.mileage ? Number(f.mileage) : null };
    try {
      const r = initial?.id ? await api.put(`/vehicles/${initial.id}`, body) : await api.post("/vehicles", body);
      toast.success("Véhicule enregistré");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(errMsg(e));
      setDup(e?.response?.data?.detail?.existing_id || null);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{initial?.id ? "Modifier le véhicule" : "Nouveau véhicule"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Immatriculation"><Input value={f.plate} onChange={set("plate")} placeholder="12345 116 16" className="font-mono-data uppercase" data-testid="vehicle-plate-input" /></Field>
          <Field label="VIN (17 caractères)"><Input value={f.vin} onChange={set("vin")} maxLength={17} className="font-mono-data uppercase" data-testid="vehicle-vin-input" /></Field>
          <Field label="Marque">
            <Input value={f.brand} onChange={set("brand")} list="vehicle-brands" placeholder="Choisir ou saisir" data-testid="vehicle-brand-input" />
            <datalist id="vehicle-brands">{VEHICLE_BRANDS.map((b) => <option key={b} value={b} />)}</datalist>
          </Field>
          <Field label="Modèle">
            <Input value={f.model} onChange={set("model")} list="vehicle-models" placeholder={f.brand ? "Choisir ou saisir" : "Choisissez d'abord la marque"} data-testid="vehicle-model-input" />
            <datalist id="vehicle-models">{(VEHICLE_MODELS[VEHICLE_BRANDS.find((b) => b.toLowerCase() === (f.brand || "").trim().toLowerCase()) || ""] || []).map((m) => <option key={m} value={m} />)}</datalist>
          </Field>
          <Field label="Année"><Input type="number" value={f.year} onChange={set("year")} data-testid="vehicle-year-input" /></Field>
          <Field label="Énergie">
            <select className={selectCls} value={f.fuel} onChange={set("fuel")} data-testid="vehicle-fuel-select">
              {["Essence", "Diesel", "GPL", "Hybride", "Électrique"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Couleur"><Input value={f.color} onChange={set("color")} data-testid="vehicle-color-input" /></Field>
          <Field label="Kilométrage"><Input type="number" value={f.mileage} onChange={set("mileage")} data-testid="vehicle-mileage-input" /></Field>
        </div>
        {dup && <Link to={`/vehicules/${dup}`} onClick={() => onOpenChange(false)} className="text-sm text-primary underline" data-testid="vehicle-duplicate-link">Ouvrir la fiche existante</Link>}
        <Button onClick={save} className="h-11" data-testid="vehicle-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ClientVehiclePicker({ value, onChange }: { value: { client: any; vehicle: any }; onChange: (v: { client: any; vehicle: any }) => void }) {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [cOpen, setCOpen] = useState(false);
  const [vOpen, setVOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => api.get("/clients", { params: { q } }).then((r) => setClients(r.data.slice(0, 8))), 200);
    return () => clearTimeout(t);
  }, [q]);

  const loadVehicles = (cid: string) => api.get(`/clients/${cid}`).then((r) => setVehicles(r.data.vehicles));
  useEffect(() => {
    if (value.client) loadVehicles(value.client.id);
  }, [value.client]);

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="eyebrow">1. Client</span>
          <Button size="sm" variant="ghost" onClick={() => setCOpen(true)} data-testid="picker-new-client-btn"><Plus className="mr-1 h-4 w-4" />Nouveau</Button>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom ou téléphone" data-testid="picker-client-search" />
        <div className="mt-2 flex max-h-56 flex-col gap-1 overflow-y-auto">
          {clients.map((c) => (
            <button key={c.id} onClick={() => onChange({ client: c, vehicle: null })} data-testid={`picker-client-${c.id}`}
              className={`rounded-sm border px-3 py-2 text-left text-sm transition-colors ${value.client?.id === c.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-muted-foreground">{c.phone}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="eyebrow">2. Véhicule</span>
          {value.client && <Button size="sm" variant="ghost" onClick={() => setVOpen(true)} data-testid="picker-new-vehicle-btn"><Plus className="mr-1 h-4 w-4" />Nouveau</Button>}
        </div>
        {!value.client && <div className="text-sm text-muted-foreground">Choisissez d'abord un client.</div>}
        <div className="flex flex-col gap-1">
          {value.client && vehicles.length === 0 && <div className="text-sm text-muted-foreground">Aucun véhicule : ajoutez-en un.</div>}
          {vehicles.map((v) => (
            <button key={v.id} onClick={() => onChange({ ...value, vehicle: v })} data-testid={`picker-vehicle-${v.id}`}
              className={`rounded-sm border px-3 py-2 text-left transition-colors ${value.vehicle?.id === v.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
              <div className="font-mono-data font-semibold">{v.plate}</div>
              <div className="text-xs text-muted-foreground">{v.brand} {v.model} {v.mileage ? `· ${v.mileage} km` : ""}</div>
            </button>
          ))}
        </div>
      </div>
      <ClientDialog open={cOpen} onOpenChange={setCOpen} onSaved={(c) => onChange({ client: c, vehicle: null })} />
      {value.client && <VehicleDialog open={vOpen} onOpenChange={setVOpen} clientId={value.client.id} onSaved={(v) => { loadVehicles(value.client.id); onChange({ ...value, vehicle: v }); }} />}
    </div>
  );
}
